import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ArrowLeftRight, Check } from 'lucide-react';
import { useAmendMode, AmendBanner } from './useAmendMode';
import './NativeInventoryAdjustmentNew.css';

// Inventory Transfer (Native) — move stock between locations without NetSuite
// in the loop. Pick a from-location and a to-location (same subsidiary — the
// server refuses cross-subsidiary), add item lines with quantities; each line
// shows live native on-hand at the from-location. Posting: ledger quantity
// movement (−qty at from, +qty at to), asset↔asset JE (debit @ to, credit @
// from — the mirrored NS InvTrnfr shape), best-effort NetSuite
// inventoryTransfer (echo-deduped). No costing leg: layers are item-grain.
// Reuses the adjustment screen's stylesheet (same house template).

const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};
const newRef = () => `TRF-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export default function NativeTransferNew() {
  const [locations, setLocations] = useState([]);
  const [fromLocation, setFromLocation] = useState(null);
  const [toLocation, setToLocation] = useState(null);
  const [itemQuery, setItemQuery] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [lines, setLines] = useState([]); // { item: {id, sku, name}, quantity: '' }
  const [onHand, setOnHand] = useState({}); // itemId -> on hand at fromLocation
  const [memo, setMemo] = useState('');
  const [allowNegative, setAllowNegative] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [writesEnabled, setWritesEnabled] = useState(null);
  const sourceKey = useRef(newRef());
  const amend = useAmendMode();
  const [amendOfNumber, setAmendOfNumber] = useState(null);

  useEffect(() => {
    axios.get('/api/native-inventory-adjustments/locations')
      .then(({ data }) => setLocations((data.locations || []).filter((l) => l.is_active !== false)))
      .catch(() => setLocations([]));
    axios.get('/api/native-transfers')
      .then(({ data }) => setWritesEnabled(data.writesEnabled === true))
      .catch(() => setWritesEnabled(null));
  }, []);

  // Amend (?amendOf=<transfer id>): prefill both locations and every line from
  // the transfer being corrected, so the operator edits rather than retypes.
  useEffect(() => {
    if (!amend.amendOf || locations.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-transfers/${encodeURIComponent(amend.amendOf)}`);
        if (cancelled || !data.transfer) return;
        const t = data.transfer;
        const from = locations.find((l) => l.id === t.from_location_id);
        const to = locations.find((l) => l.id === t.to_location_id);
        if (from) setFromLocation(from);
        if (to) setToLocation(to);
        setMemo(t.memo || '');
        setAmendOfNumber(t.transfer_number);
        setLines((data.lines || []).map((l) => ({
          item: { id: l.item_id, sku: l.sku, name: l.sku },
          quantity: String(Number(l.quantity)),
        })));
      } catch { /* prefill is best-effort; the form stays blank */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Item search (debounced) — any inventory item.
  useEffect(() => {
    if (itemQuery.trim().length < 2) { setItemResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-items', { params: { q: itemQuery.trim(), pageSize: 15 } });
        setItemResults(data.items || []);
      } catch {
        setItemResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [itemQuery]);

  // Live native on-hand at the from-location for every line.
  useEffect(() => {
    setOnHand({});
    if (!fromLocation || lines.length === 0) return undefined;
    let cancelled = false;
    axios.get('/api/native-transfers/on-hand', {
      params: { locationId: fromLocation.id, items: lines.map((l) => l.item.id).join(',') },
    })
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        for (const r of data.onHand || []) map[r.itemId] = Number(r.onHand);
        setOnHand(map);
      })
      .catch(() => { if (!cancelled) setOnHand({}); });
    return () => { cancelled = true; };
  }, [fromLocation, lines]);

  const addLine = (item) => {
    if (lines.some((l) => l.item.id === item.id)) return;
    setLines((prev) => [...prev, { item: { id: item.id, sku: item.sku, name: item.name }, quantity: '' }]);
    setItemQuery('');
    setItemResults([]);
  };
  const setLineQty = (itemId, value) => {
    setLines((prev) => prev.map((l) => (l.item.id === itemId ? { ...l, quantity: value } : l)));
  };
  const removeLine = (itemId) => setLines((prev) => prev.filter((l) => l.item.id !== itemId));

  const validLines = lines.filter((l) => Number(l.quantity) > 0);
  const shortages = fromLocation
    ? validLines.filter((l) => (onHand[l.item.id] ?? 0) < Number(l.quantity))
    : [];
  const sameLocation = fromLocation && toLocation && fromLocation.id === toLocation.id;
  const canPost = fromLocation && toLocation && !sameLocation
    && validLines.length === lines.length && lines.length > 0
    && (shortages.length === 0 || allowNegative)
    && (!amend.isAmend || amend.amendReasonValid)
    && writesEnabled !== false && !submitting;

  const post = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(amend.postUrl('/api/native-transfers'), {
        sourceKey: sourceKey.current,
        fromLocationId: fromLocation.id,
        toLocationId: toLocation.id,
        lines: validLines.map((l) => ({ itemIdOrSku: l.item.id, quantity: Number(l.quantity) })),
        memo: memo.trim() || undefined,
        allowNegative: allowNegative || undefined,
        ...amend.bodyExtras(),
      });
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      sourceKey.current = newRef(); // a failed post gets a fresh idempotency key
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const t = confirmation.transfer;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{t.transfer_number} posted</h2>
          <div className="nia-confirm-grid">
            <span>Moved</span><strong>{qty(t.total_qty)} units across {t.line_count} line{t.line_count === 1 ? '' : 's'}</strong>
            <span>From</span><strong>{fromLocation?.name}</strong>
            <span>To</span><strong>{toLocation?.name}</strong>
            <span>Quantity leg</span><strong>{t.movement_status}</strong>
            <span>GL</span><strong>{t.gl_status}{confirmation.gl ? ` (${money(confirmation.gl.totalDebit)})` : ''}</strong>
            <span>NetSuite</span><strong>{t.ns_status}{t.ns_transaction_id ? ` (${t.ns_transaction_id})` : ''}</strong>
          </div>
          <p className="nia-note">{confirmation.netsuiteNote}</p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>New transfer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><ArrowLeftRight size={20} /> {amend.isAmend ? 'Amend Inventory Transfer' : 'Inventory Transfer (Native)'}</h1>
        {writesEnabled === false && (
          <div className="nia-banner">Posting is disabled (NATIVE_TRANSFERS_ENABLED is off on the Run service). The form works; posting will 403.</div>
        )}
      </div>

      <AmendBanner amend={amend} docLabel={amendOfNumber} what="the transfer" />

      <div className="nia-card">
        <h3>1. From location</h3>
        <div className="nia-locations">
          {locations.map((l) => (
            <button
              key={l.id}
              className={`nia-location ${fromLocation?.id === l.id ? 'selected' : ''}`}
              onClick={() => setFromLocation(l)}
            >
              {l.name}
            </button>
          ))}
        </div>
      </div>

      <div className="nia-card">
        <h3>2. To location</h3>
        <div className="nia-locations">
          {locations.map((l) => (
            <button
              key={l.id}
              className={`nia-location ${toLocation?.id === l.id ? 'selected' : ''}`}
              onClick={() => setToLocation(l)}
            >
              {l.name}
            </button>
          ))}
        </div>
        {sameLocation && <div className="nia-error" style={{ marginTop: 8 }}>From and To must be different locations.</div>}
        <p className="nia-hint" style={{ marginTop: 8 }}>
          Both locations must be in the same subsidiary — cross-subsidiary transfers are not supported natively (use NetSuite).
        </p>
      </div>

      <div className="nia-card">
        <h3>3. Items</h3>
        <input
          className="nia-input nia-input-search"
          placeholder="Search items by SKU or name…"
          value={itemQuery}
          onChange={(e) => setItemQuery(e.target.value)}
        />
        {itemResults.length > 0 && (
          <div className="nia-results">
            {itemResults.map((r) => (
              <button key={r.id} className="nia-result" onClick={() => addLine(r)} disabled={lines.some((l) => l.item.id === r.id)}>
                <code>{r.sku}</code> {r.name}
                {r.onHand != null && <span className="nia-result-muted">on hand {qty(r.onHand)}</span>}
              </button>
            ))}
          </div>
        )}

        {lines.length > 0 && (
          <table className="nia-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Item</th>
                <th className="r">On hand at {fromLocation ? fromLocation.name : 'from-location'}</th>
                <th className="r">Quantity to move</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const available = onHand[l.item.id];
                const short = fromLocation && Number(l.quantity) > 0 && (available ?? 0) < Number(l.quantity);
                return (
                  <tr key={l.item.id} className={short ? 'nia-short' : ''}>
                    <td><code>{l.item.sku}</code> {l.item.name}</td>
                    <td className="r">
                      {fromLocation ? qty(available ?? 0) : '—'}
                      {short && <span className="nia-cost-source nia-cost-none"> short {qty(Number(l.quantity) - (available ?? 0))}</span>}
                    </td>
                    <td className="r">
                      <input
                        className="nia-input nia-input-num"
                        inputMode="decimal"
                        value={l.quantity}
                        onChange={(e) => setLineQty(l.item.id, e.target.value)}
                      />
                    </td>
                    <td className="r">
                      <button className="nia-icon-btn" onClick={() => removeLine(l.item.id)}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
            Memo (optional)
            <input className="nia-input" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
          </label>
        </div>
        {shortages.length > 0 && (
          <label className="nia-hint" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
            <input type="checkbox" checked={allowNegative} onChange={(e) => setAllowNegative(e.target.checked)} />
            Post anyway — let {fromLocation?.name} go negative for the short line{shortages.length === 1 ? '' : 's'}
          </label>
        )}
      </div>

      {error && <div className="nia-error">{error}</div>}

      <div className="nia-card">
        <div className="nia-footer">
          <div className="nia-footer-totals">
            <span>
              {validLines.length} line{validLines.length === 1 ? '' : 's'},{' '}
              {qty(validLines.reduce((s, l) => s + Number(l.quantity), 0))} units
            </span>
          </div>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={post} disabled={!canPost}>
              {submitting ? 'Posting…' : amend.isAmend ? 'Reverse and repost' : 'Post transfer'}
            </button>
          </div>
        </div>
        <p className="nia-hint" style={{ marginTop: 8 }}>
          Posting moves quantity out of the from-location and into the to-location immediately (native ledger), posts a
          journal entry moving inventory value between the two location dimensions, and creates a matching NetSuite
          inventory transfer when reachable. Cost layers are untouched — transfers do not change an item's cost.
        </p>
      </div>
    </div>
  );
}
